
import {Injectable, ModuleWithProviders, NgModule} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import * as moment from 'moment';
import * as filesaver from 'file-saver';
import {WebsocketService} from '../websocket/websocket.service';
import {throwError as observableThrowError, of, forkJoin} from 'rxjs';
import {map, catchError, flatMap, concat} from 'rxjs/operators';
import {SnackBarMessageService} from '../../local/snack-bar-message/snack-bar-message.service';
import {LoadingBarService} from '@ngx-loading-bar/core';
import {BroadcasterService} from '../../local/broadcaster/broadcaster.service';

@Injectable()
export class RequestService {

  constructor(private http: HttpClient,
              private websocketService: WebsocketService,
              private snackBarService: SnackBarMessageService,
              private loadingBar: LoadingBarService,
              private broadcaster: BroadcasterService) {
  }

  getRequest(req): any {
    req = this.configChecker(req);
    if (req.options && req.options.params) {
      req.options.params = this.paramChecker(req.options.params);
    }

    return this.http.get(req.url, req.options).pipe(map(result => {
      return result;
    }),
      catchError(err => {
        if (req.config.errorToast) {
          if (err.status === 403) {
            this.snackBarService.errorSnackBar('NOT_AUTHORIZED');
          } else {
            this.snackBarService.postResponseError(err.error);
          }
        }

        if (err.error.ErrorCode === 'GSX-UNAUTHORIZED') {
          this.broadcaster.broadcast('RequestingGsxAuth');
        }

        if (err.error.ErrorCode === 'GOOGLE-API-UNAUTHORIZED') {
          this.broadcaster.broadcast('RequestingGoogleAuth');
        }

        return observableThrowError(err);
    }));
  }

  postRequest(req): any {
    return this.genericRequestHandler(this.http.post(req.url, req.data, req.options), req, 'POST');
  }

  putRequest(req): any {
    return this.genericRequestHandler(this.http.put(req.url, req.data, req.options), req, 'PUT');
  }

  deleteRequest(req, param?): any {
    return this.genericRequestHandler(this.http.request('DELETE', req.url, {
      body: req.data,
      params: param
    }), req, 'DELETE');
  }

  fileSaverPost(req, fileName) {
    req.options.observe = 'response';
    req.config = {
      waitForCompletion: false
    };
    if (req.options && req.options.params) {
      req.options.params = this.paramChecker(req.options.params);
    }
    return this.genericRequestHandler(this.http.post(req.url, req.data, req.options), req, 'POST').pipe(
      map((data: any) => {
      const blob = new Blob([data.body], {
        type: data.headers.get('Content-type')
      });
      filesaver.saveAs(blob, fileName);
    }));
  }

  fileSaverPostContentType(req, fileName) {
    req.options.observe = 'response';
    req.config = {
      waitForCompletion: false
    };
    if (req.options && req.options.params) {
      req.options.params = this.paramChecker(req.options.params);
    }
    return this.genericRequestHandler(this.http.post(req.url, req.data, req.options), req, 'POST').pipe(
      map((data: any) => {
      const blob = new Blob([data.body], {
        type: data.headers.get('Content-type')
      });
      filesaver.saveAs(blob, fileName + this.addFileEnding(blob.type));
    }));
  }

  addFileEnding(contentType) {
    switch (contentType) {
      case 'application/zip':
        return '.zip';
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return '.xlsx';
    }
  }

  fileSaverGet(req, fileName) {
    req.options.observe = 'response';
    req.config = {
      waitForCompletion: false
    };
    if (req.options && req.options.params) {
      req.options.params = this.paramChecker(req.options.params);
    }
    return this.genericRequestHandler(this.http.get(req.url, req.options), req, 'GET').pipe(
      map((data: any) => {
        const blob = new Blob([data.body], {
          type: data.headers.get('Content-type')
        });

        filesaver.saveAs(blob, fileName);
      }));
  }

  private paramChecker(params) {
    const newParams = {};
    for (const prop in params) {
      if (params.hasOwnProperty(prop)) {
        if (params[prop] === '' || params[prop] === null || typeof params[prop] === 'undefined') {

        } else {
          newParams[prop] = params[prop];
        }
      }
    }
    return newParams;
  }

  private configChecker(req) {
    if (req.config) {
      req.config.successToast = (req.config.successToast === false) ? req.config.successToast : true;
      req.config.errorToast = (req.config.errorToast === false) ? req.config.errorToast : true;
      req.config.waitForCompletion = (req.config.waitForCompletion === false) ? req.config.waitForCompletion : true;
      req.config.socketLoadingBar = (req.config.socketLoadingBar === false) ? req.config.socketLoadingBar : true;
      req.config.disableControls = (req.config.disableControls === false) ? req.config.disableControls : true;
    } else {
      req.config = {
        successToast: true,
        errorToast: true,
        waitForCompletion: true,
        socketLoadingBar: true,
        disableControls: true
      };
    }
    return req;
  }

  private genericRequestHandler(request, req, method) {
    req = this.configChecker(req);
    if ((method === 'POST' || method === 'PUT' || method === 'DELETE') && req.config.disableControls) {
      this.broadcaster.broadcast('disableFormControls', true);
    }

    return request.pipe(flatMap((data: any) => {
      if (req.config.waitForCompletion && (data.SuccessResponse === undefined || (data.SuccessResponse !== undefined && data.SuccessResponse.length !== 0))) {
        if (req.config.socketLoadingBar) {
          this.loadingBar.start();
        }
        const obsv = forkJoin([
          this.websocketService.waitForCompletion(data.CorrelationId).pipe(map(success => {
              this.broadcaster.broadcast('disableFormControls', false);
              this.loadingBar.complete();
              console.log('Resolved after waiting for websocket: ', data.CorrelationId, '. Did websocket get recieved: ',
                success, ' Time: ', moment().format('HH:mm:ss.SS'));

              if (req.config.successToast) {
                if (data.ErrorResponse && data.ErrorResponse.length > 0) {
                  this.snackBarService.multipleResponseSnackBar(data);
                } else {
                  this.snackBarService.postResponseSuccess();
                }
              }
              return data;
            })
          )
        ]);
        return obsv.pipe(map(result => result[0]));

      } else {
        console.log('Resolved directly with http request');
        this.broadcaster.broadcast('disableFormControls', false);

        if (req.config.successToast) {
          if (data && data.ErrorResponse && data.ErrorResponse.length > 0) {
            this.snackBarService.multipleResponseSnackBar(data);
          } else {
            this.snackBarService.postResponseSuccess();
          }
        }
        return of(data);
      }
    }),
    catchError(err => {
      console.log('!!!!!!Error response data for request:', err);
      this.broadcaster.broadcast('disableFormControls', false);
      if (req.config.errorToast) {
        if (err.status === 403) {
          this.snackBarService.errorSnackBar('NOT_AUTHORIZED');
        } else if (err.error && err.error.ErrorDetails && err.error.ErrorDetails.length > 0) {
          this.snackBarService.detailedErrorSnackBar(err.error);
        } else {
          this.snackBarService.postResponseError(err.error);
        }
      }

      if (err.error && err.error.ErrorCode === 'GSX-UNAUTHORIZED') {
        this.broadcaster.broadcast('RequestingGsxAuth');
      }

      if (err.error && err.error.ErrorCode === 'GOOGLE-API-UNAUTHORIZED') {
        this.broadcaster.broadcast('RequestingGoogleAuth');
      }

      return observableThrowError(err);
    }));
  }
}

@NgModule({
  imports: [],
  declarations: [],
  exports: []
})
export class RequestModule {
  static forRoot(): ModuleWithProviders<RequestModule> {
    return {
      ngModule: RequestModule,
      providers: [RequestService]
    };
  }
}
